import CancelButton from 'components/buttons/CancelButton';
import DeleteButton from 'components/buttons/DeleteButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import Loader from 'components/Loader';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import { defaultCurrencyOption, useDefaultCurrencyCode } from 'hooks/useDefaultCurrencyCode';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import React, { cloneElement, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'store';
import { useAddProjectMutation, useUpdateProjectMutation } from 'store/api/projects';
import { ProjectFormData } from 'types/projects';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { useClassifiers } from 'utils/useClassifiers';
import { useGoBack, useLeaveForm } from 'utils/useNavigateWithReturnState';
import {
    ProjectBillingFields,
    ProjectCapFields,
    ProjectCasesFields,
    ProjectOverviewFields,
    ProjectTeamFields,
} from './form-sections/ProjectFields';
import { projectFormToRequest } from './projectFormMapping';
import { useProjectDelete } from './useProjectDelete';

type Props = {
    type: 'add' | 'edit';
    initialData?: ProjectFormData;
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    projectName: 'projects.form.general',
    description: 'projects.form.general',
    openedDate: 'projects.form.general',
    closedDate: 'projects.form.general',
    isNonBillable: 'projects.form.general',
    preserveNonBillableHours: 'projects.form.general',
    isUsedTimeUsedForRemuneration: 'projects.form.general',
    supervisedByEmployee: 'projects.form.our-team',
    responsibleEmployee: 'projects.form.our-team',
    capLimit: 'projects.form.project-cap',
    capCurrencyType: 'projects.form.project-cap',
    matters: 'projects.form.connect-cases',
};

const ProjectPageForm = ({ type, initialData }: Props) => {
    useFocusedEditMode('projects');

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: ['CURRENCY'] });
    const { code: defaultCurrencyCode } = useDefaultCurrencyCode();
    // A blank project, the one form without `initialData`, starts in the firm's default currency.
    const defaultValues: ProjectFormData = {
        projectName: '',
        description: '',
        openedDate: new Date().toISOString().split('T')[0],
        closedDate: null,
        capLimit: null,
        capCurrencyType: defaultCurrencyOption(
            getValidOptionsFromClassifier<string, string>(classifiers['CURRENCY']),
            defaultCurrencyCode
        ),
        supervisedByEmployee: null,
        responsibleEmployee: null,
        matters: [],
        isUsedTimeUsedForRemuneration: false,
        isNonBillable: false,
        preserveNonBillableHours: false,
    };

    const methods = useForm<ProjectFormData>({
        defaultValues: initialData ?? defaultValues,
    });
    useCreateDialogForm(methods);
    const draft = useCreateDraft({ record: 'project', methods, toPayload: projectFormToRequest });
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = methods;
    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);
    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const { params, inDialog } = useCreateRoute();
    const { id: projectId } = params;
    const goBack = useGoBack(projectId ? `/projects/${projectId}` : '/projects');
    const leaveForm = useLeaveForm();
    const bypassNavigationBlockerRef = useRef(false);

    const [addProject] = useAddProjectMutation();
    const [updateProject] = useUpdateProjectMutation();
    const handleDelete = useProjectDelete(projectId, () => {
        bypassNavigationBlockerRef.current = true;
    });

    if (isLoadingClassifiers) return <Loader />;

    const form = { methods, classifiers };

    const getFormTitle = () => (type === 'edit' ? t('projects.form.edit-title') : t('projects.form.add-title'));

    const onSubmit = async (data: ProjectFormData, action: 'create' | 'update') => {
        try {
            if (action === 'create') {
                const request = projectFormToRequest(data);
                // A draft is created by completing it: the project keeps the draft's id.
                const created =
                    draft.draftId === null ? await addProject(request).unwrap() : await draft.complete(request);
                bypassNavigationBlockerRef.current = true;
                // The dialog stays on the page it was opened over, so it finishes with the new project instead.
                leaveForm(inDialog ? `/projects/${created.id}` : '/projects');
            } else if (action === 'update' && projectId) {
                await updateProject({ ...projectFormToRequest(data), id: Number(projectId) }).unwrap();
                bypassNavigationBlockerRef.current = true;
                leaveForm(`/projects/${projectId}`);
            }
        } catch (e) {
            console.error(e);
            logError(e);
        }
    };

    const submitCreate = () => handleSubmit((data) => onSubmit(data, 'create'))();
    const submitUpdate = () => handleSubmit((data) => onSubmit(data, 'update'))();

    const getFormHeader = () => {
        const actions: React.ReactElement[] = [];

        if (type === 'add') {
            actions.push(
                <CancelButton onClick={goBack} />,
                ...(draft.button ? [draft.button] : []),
                <SaveButton
                    permissions={['CREATE.PROJECTS']}
                    onClick={() => {
                        void submitCreate();
                    }}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit') {
            actions.push(
                <CancelButton onClick={goBack} />,
                <DeleteButton permissions={['DELETE.PROJECTS']} onClick={handleDelete} disabled={buttonsDisabled} />,
                <SaveButton
                    permissions={['UPDATE.PROJECTS']}
                    onClick={() => {
                        void submitUpdate();
                    }}
                    disabled={buttonsDisabled}
                />
            );
        }

        return (
            <ActionHeader
                title={getFormTitle()}
                actions={actions.map((button, index) => cloneElement(button, { key: `action#${index}` }))}
                onBack={goBack}
                draft={draft.detail}
            />
        );
    };

    return (
        <FormProvider {...methods}>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        {getFormHeader()}
                        <DraftSaveMode mode={draft.mode}>
                            <SectionLayout sectionErrors={sectionErrors}>
                                <Section title="projects.form.general" collapsible>
                                    <ProjectOverviewFields form={form} />
                                    <ProjectBillingFields form={form} />
                                </Section>
                                <Section title="projects.form.our-team" collapsible>
                                    <ProjectTeamFields form={form} />
                                </Section>
                                <Section title="projects.form.project-cap" collapsible>
                                    <ProjectCapFields form={form} />
                                </Section>
                                <Section title="projects.form.connect-cases" collapsible>
                                    <ProjectCasesFields form={form} />
                                </Section>
                            </SectionLayout>
                        </DraftSaveMode>
                    </FormWrapper>
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
            <ConfirmNavigation
                control={control}
                bypassRef={bypassNavigationBlockerRef}
                draft={type === 'add' ? draft.leave : null}
            />
        </FormProvider>
    );
};

export default ProjectPageForm;
